import React from 'react'
import { scroller } from 'react-scroll'
import { Button } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import { PageSource } from '@mc/track-event'
import Image from 'components/LegacyImage'
import { trackPurchaseNowClick } from '../SelfServe/utils/events'
import { PricingItem } from './PricingItem'
import styles from './PricingSection.module.scss'

interface PricingSectionProps {
  pageSource: (typeof PageSource)[keyof typeof PageSource]
}

export const PricingSection: React.FC<PricingSectionProps> = ({
  pageSource,
}) => {
  const { t } = useTranslation('@mc/next')

  return (
    <div className='mc-mt-12' id='pricing-section'>
      <div className='d-flex flex-wrap justify-content-center'>
        <div className='d-flex flex-column flex-lg-row justify-content-center mc-gap-4 mc-gap-lg-8'>
          <div
            className={`d-flex flex-column justify-content-between mc-w-100 mc-corners--md mc-gap-8 mc-p-8 mc-bg-neutral-900 ${styles.card}`}
          >
            <div>
              <div className='d-flex align-items-center mc-gap-4 mc-mb-4'>
                <div
                  className={`d-flex align-items-center justify-content-center flex-shrink-0 mc-corners--sm mc-bg-neutral-800 ${styles.iconContainer}`}
                >
                  <Image
                    src='https://static.masterclass.com/MC.png'
                    width={20}
                    height={20}
                    alt='MasterClass logo'
                    className={styles.logo}
                  />
                </div>
                <div>
                  <h3 className='mc-mb-1 mc-text-white mc-font-title mc-text--bold'>
                    {t('forBusiness.pricingSection.masterClass.title')}
                  </h3>
                  <p className='mc-text-small mc-text-color--tint'>
                    {t('forBusiness.pricingSection.masterClass.subtitle')}
                  </p>
                </div>
              </div>
              <hr className='mc-separator mc-separator--subtle mc-my-8' />
              <div className='mc-mb-6'>
                <p className='mc-text-small mc-text-color--medium mc-mb-1'>
                  {t('forBusiness.pricingSection.masterClass.startingAt')}
                </p>
                <div>
                  <span className='mc-text-white mc-font-headline2'>
                    {t('forBusiness.pricingSection.masterClass.price')}
                  </span>
                  <span className='mc-text-color--tint'>
                    {t('forBusiness.pricingSection.masterClass.priceSubtext')}
                  </span>
                </div>
              </div>
              <ul className='mc-mt-4'>
                <li className='mc-mb-4'>
                  <PricingItem>
                    {t('forBusiness.pricingSection.masterClass.feature1')}
                  </PricingItem>
                </li>
                <li className='mc-mb-4'>
                  <PricingItem>
                    {t('forBusiness.pricingSection.masterClass.feature2')}
                  </PricingItem>
                </li>
                <li className='mc-mb-4'>
                  <PricingItem>
                    {t('forBusiness.pricingSection.masterClass.feature3')}
                  </PricingItem>
                </li>
              </ul>
            </div>
            <div>
              <Button
                as='a'
                href={paths.checkoutGift({
                  gift: 'true',
                  multigift: 'true',
                })}
                className='mc-w-100 mc-mb-4'
                onClick={() => {
                  trackPurchaseNowClick({
                    pageSource,
                  })
                }}
              >
                {t('forBusiness.pricingSection.masterClass.button')}
              </Button>
              <p className='mc-text-x-small mc-text-color--medium mc-text--center'>
                {t('forBusiness.pricingSection.masterClass.disclaimer')}
              </p>
            </div>
          </div>
          <div
            className={`d-flex flex-column justify-content-between mc-w-100 mc-corners--md mc-gap-8 mc-p-8 mc-bg-neutral-900 ${styles.card}`}
          >
            <div>
              <div className='d-flex align-items-center mc-gap-4 mc-mb-4'>
                <div
                  className={`d-flex align-items-center justify-content-center flex-shrink-0 mc-corners--sm mc-bg-neutral-800 ${styles.iconContainer}`}
                >
                  <Image
                    src='https://static.masterclass.com/MC+at+W.png'
                    width={20}
                    height={20}
                    alt='MasterClass at Work logo'
                    className={styles.logo}
                  />
                </div>
                <div>
                  <h3 className='mc-mb-1 mc-text-white mc-font-title mc-text--bold'>
                    {t('forBusiness.pricingSection.masterClassAtWork.title')}
                  </h3>
                  <p className='mc-text-small mc-text-color--tint'>
                    {t('forBusiness.pricingSection.masterClassAtWork.subtitle')}
                  </p>
                </div>
              </div>
              <hr className='mc-separator mc-separator--subtle mc-my-8' />
              <div className='mc-mb-6'>
                <p className='mc-text-small mc-text-color--tint mc-mb-1'>
                  {t('forBusiness.pricingSection.masterClassAtWork.startingAt')}
                </p>
                <div>
                  <span className='mc-text-white mc-font-headline2'>
                    {t('forBusiness.pricingSection.masterClassAtWork.price')}
                  </span>
                  <span className='mc-text-color--tint'>
                    {t(
                      'forBusiness.pricingSection.masterClassAtWork.priceSubtext',
                    )}
                  </span>
                </div>
              </div>
              <ul className='mc-mt-4'>
                <li className='mc-mb-4'>
                  <PricingItem>
                    {t('forBusiness.pricingSection.masterClassAtWork.feature1')}
                  </PricingItem>
                </li>
                <li className='mc-mb-4'>
                  <PricingItem>
                    {t('forBusiness.pricingSection.masterClassAtWork.feature2')}
                  </PricingItem>
                </li>
                <li className='mc-mb-4'>
                  <PricingItem>
                    {t('forBusiness.pricingSection.masterClassAtWork.feature3')}
                  </PricingItem>
                </li>
                <li className='mc-mb-4'>
                  <PricingItem>
                    {t('forBusiness.pricingSection.masterClassAtWork.feature4')}
                  </PricingItem>
                </li>
                <li className='mc-mb-4'>
                  <PricingItem>
                    {t('forBusiness.pricingSection.masterClassAtWork.feature5')}
                  </PricingItem>
                </li>
              </ul>
            </div>
            <div>
              <Button
                as='a'
                href={paths.enterpriseCart}
                className='mc-w-100 mc-mb-4'
                onClick={() => {
                  trackPurchaseNowClick({
                    pageSource,
                  })
                }}
              >
                {t('forBusiness.pricingSection.masterClassAtWork.button')}
              </Button>
              <p className='mc-text-x-small mc-text-color--medium mc-text--center'>
                {t('forBusiness.pricingSection.masterClassAtWork.disclaimer')}
              </p>
            </div>
          </div>
        </div>
        <div className='d-flex flex-column flex-sm-row flex-gap-2 mc-text--center mc-mt-8'>
          <span className='mc-text-medium'>
            {t('forBusiness.pricingSection.largeTeamText')}
          </span>
          <a
            href='#contact-form'
            onClick={(e) => {
              e.preventDefault()
              scroller.scrollTo('contact-form', {
                duration: 700,
                smooth: true,
                offset: -70,
              })
            }}
            className='mc-text--link'
          >
            {t('forBusiness.pricingSection.largeTeamCTA')}
          </a>
        </div>
      </div>
    </div>
  )
}
